import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { AuthProvider } from "@/features/auth/AuthProvider";
import { ThemeProvider } from "@/components/theme";
import { queryClient } from "@/lib/queryClient";
import { initSentry } from "@/lib/sentry";
import { LanguageProvider } from "@/lib/i18n";
import App from "@/App";
import "@/lib/maplibre-worker";

/* ── Design system tokens (brand layer — BEFORE index.css) ───── */
import "@/styles/tokens.css";

/* ── Web fonts (self-hosted via @fontsource) ─────────────────── */
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/800.css";
import "@fontsource-variable/jetbrains-mono";

import "@/index.css";

const STALE_CHUNK_RELOAD_KEY = "sentinels_stale_chunk_reload_at";
const STALE_CHUNK_MESSAGE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

function reloadForFreshAssets(): void {
  const lastReloadAt = Number(
    sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) ?? "0",
  );
  const now = Date.now();
  if (now - lastReloadAt < 10_000) return;
  sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));
  window.location.reload();
}

window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  reloadForFreshAssets();
});

window.addEventListener("unhandledrejection", (event) => {
  const message =
    event.reason instanceof Error
      ? event.reason.message
      : String(event.reason ?? "");
  if (STALE_CHUNK_MESSAGE.test(message)) {
    event.preventDefault();
    reloadForFreshAssets();
  }
});

window.addEventListener("error", (event) => {
  if (STALE_CHUNK_MESSAGE.test(event.message)) {
    event.preventDefault();
    reloadForFreshAssets();
  }
});

/* ── MSW mock layer (DEV + explicit opt-in only) ──────────────── */
async function enableMocking(): Promise<void> {
  if (!import.meta.env.DEV) return;
  if (import.meta.env.VITE_ENABLE_MSW !== "true") return;
  const { worker } = await import("@/mocks/browser");
  await worker.start({ onUnhandledRequest: "bypass" });
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

initSentry();

enableMocking().then(() => {
  createRoot(rootElement, {
    onUncaughtError: Sentry.reactErrorHandler(),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  }).render(
    <StrictMode>
      <ThemeProvider defaultTheme="system">
        <LanguageProvider>
          <BrowserRouter>
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <App />
              </AuthProvider>
            </QueryClientProvider>
          </BrowserRouter>
        </LanguageProvider>
      </ThemeProvider>
    </StrictMode>,
  );
});
